// Catálogo visual de Broadnet (script 911): marcas con su logo/color y tarjetas
// con el monto. El cajero toca la marca, toca la tarjeta ($5) y solo escribe el
// teléfono — ya no teclea la cantidad.
//
// Nada de esto toca la trama certificada: lo que sale de aquí es el MISMO
// producto_id + monto que antes salía del desplegable y del campo de monto.
import { useMemo, useState } from 'react';
import { ArrowLeft, Pencil, Search } from 'lucide-react';
import type { BnDenominacion, BnMarca, BnProductoFull } from './shared';
import { money } from './shared';

const SIN_MARCA: BnMarca = { id: '', nombre: 'Otros', color: '#475569', logo: null, orden: 999 };
export const marcaDe = (p: BnProductoFull): BnMarca => p.marca ?? { ...SIN_MARCA, nombre: p.proveedor_nombre };

const iniciales = (n: string) => n.replace(/[^\p{L}\p{N} ]/gu, ' ').trim().split(/\s+/).slice(0, 2).map((w) => w[0]).join('').toUpperCase();
const limpiar = (s: string) => s.replace(/_/g, ' ').replace(/\s+/g, ' ').trim();

export function MarcaLogo({ marca, size = 48 }: { marca: BnMarca; size?: number }) {
  if (marca.logo) {
    return <img src={marca.logo} alt={marca.nombre} style={{ width: size, height: size }} className="rounded-xl object-contain bg-white p-1" />;
  }
  return (
    <div style={{ width: size, height: size, background: marca.color, fontSize: size * 0.38 }}
      className="flex items-center justify-center rounded-xl font-bold text-white shadow-inner">
      {iniciales(marca.nombre)}
    </div>
  );
}

// Tarjeta de un monto: la imagen subida si existe; si no, se dibuja con el color
// de la marca y el monto grande, como una tarjeta de recarga física.
export function TarjetaMonto({ marca, monto, etiqueta, imagen, moneda, seleccionada, onClick }: {
  marca: BnMarca; monto: number | null; etiqueta?: string | null; imagen?: string | null;
  moneda?: string; seleccionada?: boolean; onClick: () => void;
}) {
  const anillo = seleccionada ? 'ring-4 ring-offset-2 ring-primary' : 'hover:-translate-y-0.5 hover:shadow-lg';
  if (imagen) {
    return (
      <button type="button" onClick={onClick} title={etiqueta ?? undefined}
        className={`relative aspect-[3/2] overflow-hidden rounded-xl border bg-white transition ${anillo}`}>
        <img src={imagen} alt={etiqueta ?? String(monto)} className="h-full w-full object-cover" />
        {monto != null && (
          <span className="absolute bottom-1 right-1 rounded-md bg-black/70 px-1.5 py-0.5 text-[16px] font-bold text-white tabular-nums">
            {money(monto, moneda)}
          </span>
        )}
      </button>
    );
  }
  return (
    <button type="button" onClick={onClick}
      style={{ background: `linear-gradient(135deg, ${marca.color} 0%, ${marca.color}cc 60%, #00000055 100%)` }}
      className={`relative flex aspect-[3/2] flex-col justify-between overflow-hidden rounded-xl p-3 text-left text-white shadow transition ${anillo}`}>
      <div className="flex items-center gap-1.5">
        {marca.logo
          ? <img src={marca.logo} alt="" className="h-[32px] w-[32px] rounded bg-white object-contain p-0.5" />
          : <span className="rounded bg-white/25 px-1.5 text-[14px] font-bold">{iniciales(marca.nombre)}</span>}
        <span className="truncate text-[16px] font-semibold opacity-90">{marca.nombre}</span>
      </div>
      {monto != null ? (
        <div>
          <div className="text-[2.47rem] font-extrabold leading-none tabular-nums drop-shadow">{money(monto, moneda)}</div>
          {etiqueta && <div className="mt-0.5 truncate text-[15px] font-medium opacity-90">{etiqueta}</div>}
        </div>
      ) : (
        <div className="flex items-center gap-1.5 text-[1.47rem] font-bold"><Pencil className="h-5 w-5" /> Otro monto</div>
      )}
      <span className="pointer-events-none absolute -right-6 -top-6 h-20 w-20 rounded-full bg-white/10" />
    </button>
  );
}

// Cuántas tarjetas de monto ofrece un producto («Otro monto» no cuenta).
const opcionesDe = (p: BnProductoFull) => (p.monto_modo === 'fijo' && p.monto_fijo != null
  ? 1 : (p.denominaciones ?? []).filter((d) => d.es_activo).length);

interface Agrupado { marca: BnMarca; productos: BnProductoFull[] }
export function agruparPorMarca(productos: BnProductoFull[]): Agrupado[] {
  const m = new Map<string, Agrupado>();
  for (const p of productos) {
    const mk = marcaDe(p);
    const k = mk.id || `_${mk.nombre}`;
    if (!m.has(k)) m.set(k, { marca: mk, productos: [] });
    m.get(k)!.productos.push(p);
  }
  return [...m.values()].sort((a, b) => a.marca.orden - b.marca.orden || a.marca.nombre.localeCompare(b.marca.nombre, 'es'));
}

// Paso 1: cuadrícula de marcas (con buscador). Paso 2: lo que vende esa marca.
//  · recarga → tarjetas de monto por producto (+ «Otro monto» si el producto
//    no es de monto fijo); elegir una devuelve { producto, monto }.
//  · servicio básico → una tarjeta por servicio; devuelve { producto, monto: null }
//    (el monto lo trae la consulta de deuda).
export function SelectorCatalogo({ productos, moneda, onElegir }: {
  productos: BnProductoFull[]; moneda?: string;
  onElegir: (p: BnProductoFull, monto: number | null) => void;
}) {
  const [busca, setBusca] = useState('');
  const [marcaKey, setMarcaKey] = useState<string | null>(null);
  const grupos = useMemo(() => agruparPorMarca(productos), [productos]);
  const filtrados = useMemo(() => {
    const q = busca.trim().toLowerCase();
    if (!q) return grupos;
    return grupos.filter((g) => g.marca.nombre.toLowerCase().includes(q)
      || g.productos.some((p) => `${p.proveedor_nombre} ${p.servicio_nombre}`.toLowerCase().includes(q)));
  }, [grupos, busca]);
  const keyDe = (g: Agrupado) => g.marca.id || `_${g.marca.nombre}`;
  const actual = grupos.find((g) => keyDe(g) === marcaKey) ?? null;
  // Dentro de la marca, primero lo que más opciones ofrece (más tarjetas de
  // monto) y al final lo que tiene menos; a igual cantidad, el orden del catálogo.
  const productosMarca = useMemo(() => (actual ? [...actual.productos].sort((a, b) =>
    opcionesDe(b) - opcionesDe(a) || a.orden - b.orden || a.servicio_nombre.localeCompare(b.servicio_nombre, 'es')) : []),
  [actual]);

  if (!productos.length) {
    return <p className="py-8 text-center text-sm text-muted-foreground">No hay productos activos — configúralos en Broadnet → Productos.</p>;
  }

  if (!actual) {
    return (
      <div className="space-y-3">
        <div className="relative">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <input autoFocus value={busca} onChange={(e) => setBusca(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && filtrados.length === 1) setMarcaKey(keyDe(filtrados[0])); }}
            placeholder="Buscar operadora o servicio…" className="h-10 w-full rounded-lg border bg-background pl-9 pr-3 text-sm" />
        </div>
        <div className="grid grid-cols-3 gap-3 sm:grid-cols-4 md:grid-cols-5">
          {filtrados.map((g) => (
            <button key={keyDe(g)} type="button" onClick={() => setMarcaKey(keyDe(g))}
              className="flex flex-col items-center gap-2 rounded-xl border bg-card p-3 transition hover:-translate-y-0.5 hover:border-primary hover:shadow-md">
              <MarcaLogo marca={g.marca} size={56} />
              <span className="line-clamp-2 text-center text-sm font-medium leading-tight">{g.marca.nombre}</span>
            </button>
          ))}
        </div>
        {!filtrados.length && <p className="text-center text-sm text-muted-foreground">Nada coincide con «{busca}».</p>}
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-3">
        <button type="button" onClick={() => setMarcaKey(null)} className="inline-flex items-center gap-1 rounded-md border px-2 py-1 text-sm hover:bg-muted">
          <ArrowLeft className="h-4 w-4" /> Marcas
        </button>
        <MarcaLogo marca={actual.marca} size={36} />
        <span className="text-lg font-semibold">{actual.marca.nombre}</span>
      </div>
      <div className="max-h-[55vh] space-y-4 overflow-y-auto pr-1">
        {productosMarca.map((p) => {
          const titulo = limpiar(p.servicio_nombre);
          if (p.tipo === 'servicio_basico') {
            return (
              <button key={p.id} type="button" onClick={() => onElegir(p, null)}
                className="flex w-full items-center gap-3 rounded-xl border p-3 text-left hover:border-primary hover:bg-muted/40">
                <MarcaLogo marca={actual.marca} size={40} />
                <span className="font-medium">{titulo}</span>
              </button>
            );
          }
          const denoms: BnDenominacion[] = (p.denominaciones ?? []).filter((d) => d.es_activo);
          const fijo = p.monto_modo === 'fijo' && p.monto_fijo != null;
          return (
            <div key={p.id}>
              {actual.productos.length > 1 && <div className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">{titulo}</div>}
              <div className="grid gap-3 grid-cols-[repeat(auto-fill,minmax(min(100%,225px),225px))]">
                {fijo
                  ? <TarjetaMonto marca={actual.marca} monto={Number(p.monto_fijo)} etiqueta={titulo} moneda={moneda} onClick={() => onElegir(p, Number(p.monto_fijo))} />
                  : denoms.map((d) => (
                      <TarjetaMonto key={d.id} marca={actual.marca} monto={Number(d.monto)} etiqueta={d.etiqueta}
                        imagen={d.imagen} moneda={moneda} onClick={() => onElegir(p, Number(d.monto))} />
                    ))}
                {/* 913: el administrador decide si sale «Otro monto»; sin tarjetas activas
                    sale igual, o el producto quedaría invendible. */}
                {!fijo && (p.permite_otro_monto !== false || denoms.length === 0) && (
                  <TarjetaMonto marca={actual.marca} monto={null} moneda={moneda} onClick={() => onElegir(p, null)} />
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
